import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthLayoutComponent } from '../layout/auth-layout.component';
import { LoggerService, ValidationUtils } from '@victory-lib-ui/shared/utils';
import { ToastService } from '@victory-lib-ui/shared/utils';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  resolveAuthFailureMessageKey,
  resolvePasswordRequirementMessageKey,
} from '../../utils/auth-feedback.utils';

type ResetStep = 'request' | 'verify' | 'reset';

@Component({
  selector: 'lib-reset-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    AuthLayoutComponent,
    TranslocoModule,
  ],
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(TranslocoService);

  currentStep = signal<ResetStep>('request');
  showPassword = signal(false);
  showConfirmPassword = signal(false);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  email = signal<string>('');

  requestForm: FormGroup;
  verifyForm: FormGroup;
  resetForm: FormGroup;

  otpExpirySeconds = signal(300);
  private otpTimerInterval?: number;

  passwordRequirements = computed(() => {
    const password = this.resetForm.get('newPassword')?.value ?? '';
    return ValidationUtils.getPasswordRequirements(password);
  });

  passwordIsStrong = computed(() => {
    const reqs = this.passwordRequirements();
    return (
      reqs.hasMinLength &&
      reqs.hasUppercase &&
      reqs.hasLowercase &&
      reqs.hasNumber &&
      reqs.hasSpecialChar
    );
  });

  constructor() {
    this.requestForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.verifyForm = this.fb.group({
      otp: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    this.resetForm = this.fb.group({
      newPassword: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]],
    });
  }

  onRequestOtp(): void {
    if (this.requestForm.invalid) {
      this.markFormGroupTouched(this.requestForm);
      const key = 'reset.request_error_validation';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const email = this.requestForm.get('email')?.value ?? '';
    this.email.set(email);

    this.authService.requestPasswordReset({ email }).subscribe({
      next: (response) => {
        if (response.success) {
          this.logger.info('OTP sent successfully');
          this.successMessage.set(this.i18n.translate('reset.send_code'));
          this.toast.show('reset.send_code', 'success');
          this.currentStep.set('verify');
          this.startOtpTimer();
        } else {
          const key = 'reset.request_error_unexpected';
          this.errorMessage.set(this.i18n.translate(key));
          this.toast.show(key, 'error');
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        this.logger.error('Request OTP error', error);
        const key = resolveAuthFailureMessageKey(
          'request_password_reset',
          error
        );
        this.errorMessage.set(this.i18n.translate(key));
        this.toast.show(key, 'error');
        this.isLoading.set(false);
      },
    });
  }

  onVerifyOtp(): void {
    if (this.verifyForm.invalid) {
      this.markFormGroupTouched(this.verifyForm);
      const key = 'reset.verify_error_invalid';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const otp = this.verifyForm.get('otp')?.value;

    this.authService.verifyOTP({ email: this.email(), otp }).subscribe({
      next: (response) => {
        if (response.success) {
          this.logger.info('OTP verified successfully');
          this.successMessage.set(this.i18n.translate('reset.verify_button'));
          this.toast.show('reset.verify_button', 'success');
          this.currentStep.set('reset');
          this.stopOtpTimer();
        } else {
          const key = 'reset.verify_error_unexpected';
          this.errorMessage.set(this.i18n.translate(key));
          this.toast.show(key, 'error');
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        this.logger.error('Verify OTP error', error);
        const key = resolveAuthFailureMessageKey(
          'verify_password_reset',
          error
        );
        this.errorMessage.set(this.i18n.translate(key));
        this.toast.show(key, 'error');
        this.isLoading.set(false);
      },
    });
  }

  onResetPassword(): void {
    if (this.resetForm.invalid) {
      this.markFormGroupTouched(this.resetForm);
      const key = 'reset.reset_error_validation';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    if (!this.passwordIsStrong()) {
      const key = resolvePasswordRequirementMessageKey(
        'reset',
        this.resetForm.get('newPassword')?.value ?? ''
      );
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    if (!this.passwordsMatch) {
      const key = 'reset.passwords_mismatch';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const newPassword = this.resetForm.get('newPassword')?.value ?? '';
    const otp = this.verifyForm.get('otp')?.value ?? '';

    this.authService
      .updatePassword({
        email: this.email(),
        otp,
        password: newPassword,
      })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.logger.info('Password reset successfully');
            this.successMessage.set(this.i18n.translate('reset.reset_button'));
            this.toast.show('reset.reset_button', 'success');
            this.router.navigate(['/auth/login'], {
              queryParams: { passwordReset: 'true' },
            });
          } else {
            const key = 'reset.reset_error_unexpected';
            this.errorMessage.set(this.i18n.translate(key));
            this.toast.show(key, 'error');
            this.isLoading.set(false);
          }
        },
        error: (error) => {
          this.logger.error('Reset password error', error);
          const key = resolveAuthFailureMessageKey('reset_password', error);
          this.errorMessage.set(this.i18n.translate(key));
          this.toast.show(key, 'error');
          this.isLoading.set(false);
        },
      });
  }

  resendOtp(): void {
    this.stopOtpTimer();
    this.verifyForm.reset();
    this.errorMessage.set(null);
    this.onRequestOtp();
  }

  private startOtpTimer(): void {
    this.otpExpirySeconds.set(300);
    this.otpTimerInterval = window.setInterval(() => {
      const currentTime = this.otpExpirySeconds();
      if (currentTime > 0) {
        this.otpExpirySeconds.set(currentTime - 1);
      } else {
        this.stopOtpTimer();
        this.errorMessage.set(this.i18n.translate('reset.otp_expired'));
      }
    }, 1000);
  }

  private stopOtpTimer(): void {
    if (this.otpTimerInterval) {
      clearInterval(this.otpTimerInterval);
      this.otpTimerInterval = undefined;
    }
  }

  get formattedTimer(): string {
    const seconds = this.otpExpirySeconds();
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  get passwordsMatch(): boolean {
    const password = this.resetForm.get('newPassword')?.value ?? '';
    const confirmPassword = this.resetForm.get('confirmPassword')?.value ?? '';
    return password === confirmPassword && password.length > 0;
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword.update((show) => !show);
    } else {
      this.showConfirmPassword.update((show) => !show);
    }
  }

  goBack(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);

    if (this.currentStep() === 'verify') {
      this.stopOtpTimer();
      this.currentStep.set('request');
    } else if (this.currentStep() === 'reset') {
      this.currentStep.set('verify');
      this.startOtpTimer();
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  ngOnDestroy(): void {
    this.stopOtpTimer();
  }
}
